import { formatMsDuration, getDurationStringSeconds, isDurationString, isLocalTimeZone, normalizeTimeZone } from "@chronosphereio/core";
import { endOfMonth, isValid, setMilliseconds, startOfMonth } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { RESOLUTION_LEVELS, intervalSizeToResolution } from "./schema/index.js";
import { DEFAULT_INTERVAL_VARIABLE_RESOLUTION, DEFAULT_MIN_INTERVAL_SIZE } from "./variables.js";
const MINUTE_MS = 60000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 86400000;
const WEEK_MS = 604800000;
const MONTH_MS = 2629440000;
const YEAR_MS = 31536000000;
var time_PinStatus = /*#__PURE__*/ function(PinStatus) {
    PinStatus["PINNED"] = "pinned";
    PinStatus["PINNED_IN_DIFFERENT_TAB"] = "pinned_in_different_tab";
    PinStatus["UNPINNED"] = "unpinned";
    return PinStatus;
}({});
const MAX_DATA_POINTS_HIGH_RESOLUTION = 1920;
const MIN_STEP_SIZE_MS = 15000;
const ROUNDED_STEP_INTERVALS = [
    {
        maxMs: 17500,
        roundedStepMs: MIN_STEP_SIZE_MS
    },
    {
        maxMs: 25000,
        roundedStepMs: 20000
    },
    {
        maxMs: 45000,
        roundedStepMs: 30000
    },
    {
        maxMs: 90000,
        roundedStepMs: MINUTE_MS
    },
    {
        maxMs: 210000,
        roundedStepMs: 120000
    },
    {
        maxMs: 450000,
        roundedStepMs: 300000
    },
    {
        maxMs: 750000,
        roundedStepMs: 600000
    },
    {
        maxMs: 1050000,
        roundedStepMs: 900000
    },
    {
        maxMs: 1500000,
        roundedStepMs: 1200000
    },
    {
        maxMs: 2700000,
        roundedStepMs: 1800000
    },
    {
        maxMs: 5400000,
        roundedStepMs: HOUR_MS
    },
    {
        maxMs: 9000000,
        roundedStepMs: 7200000
    },
    {
        maxMs: 16200000,
        roundedStepMs: 10800000
    },
    {
        maxMs: 32400000,
        roundedStepMs: 21600000
    },
    {
        maxMs: DAY_MS,
        roundedStepMs: 43200000
    },
    {
        maxMs: WEEK_MS,
        roundedStepMs: DAY_MS
    },
    {
        maxMs: 1814400000,
        roundedStepMs: WEEK_MS
    },
    {
        maxMs: 3628800000,
        roundedStepMs: 2592000000
    },
    {
        maxMs: 63072000000,
        roundedStepMs: 31536000000
    }
];
function roundStepInterval(stepMs) {
    for (const { maxMs, roundedStepMs } of ROUNDED_STEP_INTERVALS)if (stepMs < maxMs) return roundedStepMs;
    return MIN_STEP_SIZE_MS;
}
function getSuggestedStepMs(timeRange, maxDatapoints) {
    const queryRangeMs = timeRange.end.valueOf() - timeRange.start.valueOf();
    const stepMs = Math.floor(queryRangeMs / maxDatapoints);
    return roundStepInterval(stepMs);
}
function isValidDate(d) {
    return isValid(d);
}
function getMonthTimeRange(monthDate = new Date(), timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    const localTimeZone = isLocalTimeZone(normalizedTimeZone);
    if ('UTC' === normalizedTimeZone) {
        const year = monthDate.getUTCFullYear();
        const month = monthDate.getUTCMonth();
        const start = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
        const end = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 0));
        return {
            start,
            end: setMilliseconds(end, 0)
        };
    }
    const zonedMonthDate = localTimeZone ? monthDate : utcToZonedTime(monthDate, normalizedTimeZone);
    const start = localTimeZone ? startOfMonth(zonedMonthDate) : utcToZonedTime(startOfMonth(zonedMonthDate), normalizedTimeZone);
    const end = localTimeZone ? endOfMonth(zonedMonthDate) : utcToZonedTime(endOfMonth(zonedMonthDate), normalizedTimeZone);
    return {
        start,
        end: setMilliseconds(end, 0)
    };
}
var time_DATE_FORMAT = /*#__PURE__*/ function(DATE_FORMAT) {
    DATE_FORMAT["YEAR"] = "yyyy";
    DATE_FORMAT["SHORT"] = "MM/dd/yyyy";
    DATE_FORMAT["MEDIUM"] = "MMM dd, yyyy";
    DATE_FORMAT["MEDIUM_NO_YEAR"] = "MMM dd";
    return DATE_FORMAT;
}({});
var time_TIME_FORMAT = /*#__PURE__*/ function(TIME_FORMAT) {
    TIME_FORMAT["SHORT_12"] = "hh:mm aa";
    TIME_FORMAT["SHORT_24"] = "HH:mm";
    TIME_FORMAT["MEDIUM_12"] = "hh:mm:ss aa";
    TIME_FORMAT["MEDIUM_24"] = "HH:mm:ss";
    return TIME_FORMAT;
}({});
const DATE_TIME_FORMAT_12 = "MM/dd/yyyy hh:mm:ss aa";
const DATE_TIME_FORMAT_12_SHORT_TIME = "MM/dd/yyyy hh:mm aa";
function isValidResolutionLevel(resolution) {
    return 'string' == typeof resolution && RESOLUTION_LEVELS.includes(resolution);
}
function isValidResolution(resolution) {
    return 'string' == typeof resolution && (isValidResolutionLevel(resolution) || isDurationString(resolution));
}
function convertMsToSeconds(value) {
    return Math.round(value / 1000);
}
function calculateAutoIntervalSeconds({ timeRange, resolution, minInterval }) {
    const minIntervalString = minInterval && isDurationString(minInterval) ? minInterval : DEFAULT_MIN_INTERVAL_SIZE;
    const minIntervalSizeSeconds = getDurationStringSeconds(minIntervalString);
    const autoResolution = isValidResolutionLevel(resolution) ? resolution : DEFAULT_INTERVAL_VARIABLE_RESOLUTION;
    const maxDatapoints = mapResolutionToMaxDatapoints(autoResolution);
    const resolutionAdjustedStepMs = getSuggestedStepMs(timeRange, maxDatapoints);
    const resolutionAdjustedStepSeconds = convertMsToSeconds(resolutionAdjustedStepMs);
    if (void 0 === minIntervalSizeSeconds) return `${resolutionAdjustedStepSeconds}s`;
    return `${Math.max(minIntervalSizeSeconds, resolutionAdjustedStepSeconds)}s`;
}
function calculateAutoIntervalFormattedDuration({ timeRange, intervalSize, minInterval }) {
    const resolution = intervalSizeToResolution[intervalSize];
    const intervalSeconds = calculateAutoIntervalSeconds({
        timeRange,
        resolution,
        minInterval
    });
    const seconds = parseInt(intervalSeconds, 10);
    return formatMsDuration(1000 * seconds);
}
const MAX_DATA_POINTS_FALLBACK = 300;
function mapResolutionToMaxDatapoints(resolutionLevel, maxDatapoints = MAX_DATA_POINTS_FALLBACK) {
    const resolutionToMaxDatapoints = {
        Low: 0.25 * maxDatapoints,
        Medium: maxDatapoints,
        High: MAX_DATA_POINTS_HIGH_RESOLUTION
    };
    return resolutionToMaxDatapoints[resolutionLevel] ?? resolutionToMaxDatapoints.Medium;
}
export { time_DATE_FORMAT as DATE_FORMAT, DATE_TIME_FORMAT_12, DATE_TIME_FORMAT_12_SHORT_TIME, DAY_MS, HOUR_MS, MAX_DATA_POINTS_HIGH_RESOLUTION, MINUTE_MS, MIN_STEP_SIZE_MS, MONTH_MS, time_PinStatus as PinStatus, time_TIME_FORMAT as TIME_FORMAT, WEEK_MS, YEAR_MS, calculateAutoIntervalFormattedDuration, calculateAutoIntervalSeconds, convertMsToSeconds, getMonthTimeRange, getSuggestedStepMs, isValidDate, isValidResolution, isValidResolutionLevel, mapResolutionToMaxDatapoints, roundStepInterval };

//# sourceMappingURL=time.js.map